import React, { useEffect, useRef, useState } from 'react';
import './GraphDetails.scss';
import axios from 'axios';
import { API_URL, CANVAS_HEIGHT, CANVAS_WIDTH } from '../../../Constants';
import { handleAxiosResponse } from '../../../Util';
import { isEmpty } from 'lodash';
import SankeyUtil from '../Graph/SankeyUtil';
import { validateCsvFile } from '../Graph/CSV_util';
import { FaRegCopy } from 'react-icons/fa';
import Upload from '../../../common/Upload/Upload';

export default function GraphDetails({graphRecord, onGraphSaved, onHighLightModeChanged, getGraphMetadata}) {

    const MODES = {
        NEW: 'new',
        EDIT: 'edit'
    };
    const linksUploadControl = useRef(null);
    const groupsUploadControl = useRef(null);
    const addonsUploadControl = useRef(null);
    const DEFAULT_HIGHLIGHT_MODE = SankeyUtil.HIGHLIGHT_MODE.COLOR;

    const [mode, setMode] = useState(isEmpty(graphRecord)? MODES.NEW: MODES.EDIT);
    const [linksFile, setLinksFile] = useState(null);
    const [groupsFile, setGroupsFile] = useState(null);
    const [addonsFile, setAddonsFile] = useState(null);
    const [name, setName] = useState('');
    const [highlightMode, setHighlightMode] = useState(DEFAULT_HIGHLIGHT_MODE);
    const [copied, setCopied] = useState(false);
    const [errors, setErrors] = useState([]);

    useEffect(()=>{
        // clear form
        if(!isEmpty(graphRecord)){
            setMode(MODES.EDIT);
            setName(graphRecord.name);
            setHighlightMode(graphRecord.highlightMode || DEFAULT_HIGHLIGHT_MODE);
        }else{
            setMode(MODES.NEW);
            linksUploadControl.current.clear();
            groupsUploadControl.current.clear();
            addonsUploadControl.current.clear();
            setName('');
            setHighlightMode(DEFAULT_HIGHLIGHT_MODE);
        }
    }, [graphRecord]);


    const handleLinksChange = (event) => {
        const files = event.target.files;
        if(files.length > 0){
            const file = files[0];
            if(file.type !== 'text/csv'){
                linksUploadControl.clear();
                setErrors(['Solo archivos CSV soportados']);
                return;
            }
            validateCsvFile(file, 'links')
            .then(valid=>{
                setLinksFile(file);
                setErrors([]);
            }).catch(error=>{
                console.error(error);
                setLinksFile(null);
                setErrors(['El formato del archivo "CSV de relaciones" no es el correcto']);
            });
            
        }
    }

    const handleGroupsChange = (event) => {
        const files = event.target.files;
        if(files.length > 0){
            const file = files[0];
            if(file.type !== 'text/csv'){
                groupsUploadControl.clear();
                setErrors(['Solo archivos CSV soportados']);
                return;
            }
            validateCsvFile(file, 'groups')
            .then(valid=>{
                setGroupsFile(file);
                setErrors([]);
            }).catch(error=>{
                console.error(error);
                setLinksFile(null);
                setErrors(['El formato del archivo "CSV de agrupaciones" no es el correcto']);
            });
            
        }
    }

    const handleAddonsChange = (event) => {
        const files = event.target.files;
        if(files.length > 0){
            const file = files[0];
            if(file.type !== 'text/csv'){
                addonsUploadControl.clear();
                setErrors(['Solo archivos CSV soportados']);
                return;
            }
            validateCsvFile(file, 'addons')
            .then(valid=>{
                setAddonsFile(file);
                setErrors([]);
            }).catch(error=>{
                console.error(error);
                setLinksFile(null);
                setErrors(['El formato del archivo "CSV de agrupaciones" no es el correcto']);
            });
        }
    }

    const handleNameChange = (event) => {
        setName(event.target.value);
    }

    const isFormValid = function(){
        const errors = [];

        if(mode === MODES.NEW){
            if(!linksFile){
                errors.push('Se necesita el archivo de relaciones');
            }
            if(!groupsFile){
                errors.push('Se necesita el archivo de agrupaciones');
            }
            // links file is optional
        }
        if(isEmpty(name)){
            errors.push('Se necesita un nombre para la gráfica');
        }
        setErrors(errors);
        return errors.length === 0;
    }

    const onSave = async (event) => {
        event.preventDefault();
        
        if(!isFormValid()){
            return;
        }

        try{
            // upload links file
            let linksData;
            let groupsData;
            let addonsData;
            
            if(linksFile){
                const formData1 = new FormData();
                formData1.append('file', linksFile);
                const serverResponse1 = await axios.post(`${API_URL}/upload-file`, formData1, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                });
                linksData = handleAxiosResponse(serverResponse1);
            }
            

            // upload groups file
            if(groupsFile){
                const formData2 = new FormData();
                formData2.append('file', groupsFile);
                const serverResponse2 = await axios.post(`${API_URL}/upload-file`, formData2, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                });
                groupsData = handleAxiosResponse(serverResponse2);
            }

            // uploads addon file
            if(addonsFile){
                const formData3 = new FormData();
                formData3.append('file', addonsFile);
                const serverResponse3 = await axios.post(`${API_URL}/upload-file`, formData3, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                });
                addonsData = handleAxiosResponse(serverResponse3);
            }
            
            // create graph record
            const payload = {
                name: name,
                metadata: getGraphMetadata(),
                canvasWidth: CANVAS_WIDTH,
                canvasHeight: CANVAS_HEIGHT,
                highlightMode: highlightMode

            };
            if(linksData){
                payload.linksFile = linksData.fileUrl;
                payload.linksOriginalFilename = linksData.originalName;
            }
            if(groupsData){
                payload.groupsFile = groupsData.fileUrl;
                payload.groupsOriginalFilename = groupsData.originalName;
            }
            if(addonsData){
                payload.addonsFile = addonsData.fileUrl;
                payload.addonsOriginalFilename = addonsData.originalName;
            }

            let response, savedRecord;
            if(mode === MODES.NEW){
                response = await axios.post(`${API_URL}/graph`, payload);
            }else{
                response = await axios.patch(`${API_URL}/graph/${graphRecord.id}`, payload);
            }

            savedRecord = handleAxiosResponse(response);

            onGraphSaved(savedRecord.id, mode);

        }catch(error){
            setErrors(['Error subiendo los archivos']);
            console.error(error);
        }
    }

    const onHighlightModeChange = (event) => {
        const newValue = event.target.value;
        setHighlightMode(newValue);
        // send event to the parent
        onHighLightModeChanged(newValue);
    }

    const buildGraphUrl = (id) => {
        return `${window.location.origin}/graph/${id}`;
    }

    const onCopy = async () => {
        try {
            const textToCopy = buildGraphUrl(graphRecord.id);
            navigator.clipboard.writeText(textToCopy).then(()=>{
                setCopied(true);
                setTimeout(()=>{
                    setCopied(false);
                }, 1000)
            });
        } catch (err) {
            console.error('Error al copiar el texto: ', err);
        }
    };

    return (
        graphRecord && <form className="graph-details-edit" onSubmit={onSave}>
            <div className='details-container'>
                <div className='left'>
                    <div className='row'>
                        <Upload ref={linksUploadControl} labelText={'CSV relaciones '} onChange={handleLinksChange} accept={'.csv, text/csv'} buttonText='Subir' textFilename={graphRecord.linksOriginalFilename} labelClass={'upload-label'}></Upload>
                    </div>
                    <div className='row'>
                        <Upload ref={groupsUploadControl} labelText={'CSV agrupaciones '} onChange={handleGroupsChange} accept={'.csv, text/csv'} buttonText='Subir' textFilename={graphRecord.groupsOriginalFilename} labelClass={'upload-label'}></Upload>
                    </div>
                    <div className='row'>
                        <Upload ref={addonsUploadControl} labelText={'CSV agregaciones '} onChange={handleAddonsChange} accept={'.csv, text/csv'} buttonText='Subir' textFilename={graphRecord.addonsOriginalFilename} labelClass={'upload-label'}></Upload>
                    </div>
                    <div className='row'>
                        <div className='name-details'></div>
                        <span className="label">Nombre</span>
                        <input className="new-graph-name" id='new-graph-name' type="text" onChange={handleNameChange} value={name}/>
                        
                        <div className='highlight-details'>
                            <span className="label">Modo de resaltado: </span>
                            <label htmlFor="">
                                <input type="radio" className='highlight-option' 
                                    value={SankeyUtil.HIGHLIGHT_MODE.COLOR} checked={highlightMode === SankeyUtil.HIGHLIGHT_MODE.COLOR} 
                                    onChange={onHighlightModeChange}
                                />
                                Colores
                            </label>
                            <label htmlFor="">
                                <input type="radio" className='highlight-option' 
                                    value={SankeyUtil.HIGHLIGHT_MODE.BLACK_AND_WHITE} checked={highlightMode === SankeyUtil.HIGHLIGHT_MODE.BLACK_AND_WHITE}
                                    onChange={onHighlightModeChange}
                                />
                                Blanco y negro
                            </label>
                        </div>
                    </div>
                    
                    <div className='row'>
                        {
                            !isEmpty(graphRecord) && graphRecord.id &&
                            <div className='url-details'>
                                <span className='url-label'>URL:</span><span className='graph-url'>{buildGraphUrl(graphRecord.id)}</span>  
                                <button className='icon-button copy-button' type="button" title="Copiar" onClick={onCopy}><FaRegCopy /></button>
                                {
                                    copied && <span className='copied-text'>Copiado!</span>
                                }
                            </div>
                        }
                    </div>
                    <div className='row'>
                        { !isEmpty(errors) && errors.map((error, index) => (
                            <p key={index} className='error'>{error}</p>
                        ))}
                    </div>
                </div>
                <div className='right buttons'>
                    <button className='save-btn primary-button' type='submit'>Guardar</button>
                </div>
            </div>
        </form>
    )
}
